import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  FileImage,
  FolderOpen,
  Users,
  ShoppingCart,
  DollarSign,
  LogOut,
  ArrowLeft,
  Type,
  CreditCard,
  Phone,
  AlertCircle,
  Menu,
  X,
  Handshake,
  PhoneCall,
  MessageSquare,
} from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-2">
    <div className="w-8 h-8 bg-gold/20 rounded-lg flex items-center justify-center">
      <span className="font-script text-gold text-lg">A</span>
    </div>
    <span className="font-script text-2xl text-gold">Azamein</span>
    <span className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Admin</span>
  </div>
);

const menuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/templates', icon: FileImage, label: 'Templates' },
  { path: '/admin/categories', icon: FolderOpen, label: 'Categories' },
  { path: '/admin/ivr', icon: Phone, label: 'IVR Management' },
  { path: '/admin/calls', icon: PhoneCall, label: 'Calls' },
  { path: '/admin/sms-inbox', icon: MessageSquare, label: 'SMS Inbox', badge: 'sms' },
  { path: '/admin/fonts', icon: Type, label: 'Fonts' },
  { path: '/admin/customers', icon: Users, label: 'Customers' },
  { path: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
  { path: '/admin/transactions', icon: CreditCard, label: 'Transactions' },
  { path: '/admin/pricing', icon: DollarSign, label: 'Pricing' },
  { path: '/admin/partners', icon: Handshake, label: 'Partners' },
  { path: '/admin/error-logs', icon: AlertCircle, label: 'Error Logs' },
];

/**
 * Admin navigation. Fixed sidebar on large screens; on smaller screens it
 * becomes a drawer opened from a top bar (see `.admin-sidebar ~ .ml-64` in
 * App.css, which removes the desktop content offset).
 */
export const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadSupport, setUnreadSupport] = useState(0);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // SMS support cases waiting for an answer (badge on "SMS Inbox"), refreshed every minute.
  useEffect(() => {
    let alive = true;
    const check = () => axios.get(`${API_URL}/admin/sms/conversations`, { params: { filter: 'unread', limit: 1 } })
      .then((r) => { if (alive) setUnreadSupport(r.data?.unread_cases || 0); })
      .catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 admin-sidebar text-white flex items-center justify-between px-4 shadow">
        <Link to="/admin" aria-label="Admin dashboard">
          <Logo />
        </Link>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="p-2 rounded-md hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          aria-label="Open admin menu"
          aria-expanded={open}
          aria-controls="admin-sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        id="admin-sidebar"
        className={`admin-sidebar w-64 h-screen fixed left-0 top-0 z-50 text-white flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Admin navigation"
      >
        <div className="p-6 pb-4 flex items-center justify-between">
          <Link to="/admin" data-testid="admin-logo" className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold">
            <Logo />
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="lg:hidden p-1.5 rounded-md text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Close admin menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-white mb-6 text-sm transition-colors"
            data-testid="back-to-site"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to Site
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 space-y-1">
          {menuItems.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={active ? 'page' : undefined}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                  active ? 'bg-gold text-ink font-medium' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                {item.label}
                {item.badge === 'sms' && unreadSupport > 0 && (
                  <span className="ml-auto rounded-full bg-red-500 text-white text-xs font-semibold px-2 py-0.5" aria-label={`${unreadSupport} unread`}>
                    {unreadSupport}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-6 border-t border-white/10">
          {user?.email && <p className="px-4 pb-2 text-xs text-white/40 truncate">{user.email}</p>}
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 w-full text-white/70 hover:bg-white/10 hover:text-white rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
};
